import { supabase } from '../lib/supabase';

export interface CRMProspect {
  id?: string;
  organization_name: string;
  sector?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  source?: string;
  tag?: string;
  prenom?: string;
  nom?: string;
  fonction?: string;
  utm_source?: string;
  utm_campaign?: string;
  ip_address?: string;
  simulation_data?: Record<string, unknown>;
  estimated_cash: number;
  estimated_monthly_expenses: number;
  next_major_due_date?: string;
  next_major_due_amount?: number;
  survival_months?: number;
  spt_score?: number;
  dynamic_probability?: number;
  imf_score: number;
  declic_reached: boolean;
  declic_phrase?: string;
  targeted_offer?: string;
  estimated_mrr: number;
  status: CRMProspectStatus;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_modified_by?: string;
}

export type CRMProspectStatus =
  | 'insomnie_detectee'
  | 'probleme_formule'
  | 'demo_realisee'
  | 'projection_envoyee'
  | 'decision_en_cours'
  | 'active'
  | 'pilote_actif'
  | 'perdu';

export interface CRMInteraction {
  id?: string;
  prospect_id: string;
  interaction_type: 'appel' | 'email' | 'demo' | 'reunion' | 'proposition' | 'autre';
  subject: string;
  notes?: string;
  outcome?: string;
  next_action?: string;
  next_action_date?: string;
  created_at?: string;
  created_by?: string;
}

export interface CRMNote {
  id?: string;
  prospect_id: string;
  note: string;
  is_important: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_modified_by?: string;
}

export interface CRMAnalytics {
  total_prospects: number;
  active_pipeline: number;
  spt_critical: number;
  spt_high: number;
  spt_medium: number;
  spt_low: number;
  total_potential_mrr: number;
  weighted_mrr: number;
  avg_stress_level: number;
  avg_conversion_probability: number;
}

export interface PipelineStats {
  status: CRMProspectStatus;
  count: number;
  total_mrr: number;
  weighted_mrr: number;
  avg_spt: number;
}

export const STATUS_LABELS: Record<CRMProspectStatus, string> = {
  insomnie_detectee: 'Insomnie Détectée',
  probleme_formule: 'Problème Formulé',
  demo_realisee: 'Démo Réalisée',
  projection_envoyee: 'Projection Envoyée',
  decision_en_cours: 'Décision en Cours',
  active: 'Client Actif',
  pilote_actif: 'Pilote Actif',
  perdu: 'Perdu',
};

export const SPT_CONFIG = {
  1: { label: 'Faible', color: 'blue', description: '6+ mois de trésorerie' },
  2: { label: 'Moyen', color: 'yellow', description: '3-6 mois de trésorerie' },
  3: { label: 'Élevé', color: 'orange', description: '1-3 mois de trésorerie' },
  4: { label: 'Critique', color: 'red', description: '< 1 mois de trésorerie' },
};

/**
 * Calcule le nombre de mois de survie avant tension de trésorerie
 */
export function calculateSurvivalMonths(
  cash: number,
  monthlyExpenses: number
): number {
  if (monthlyExpenses <= 0) return 999;
  return Math.round((cash / monthlyExpenses) * 10) / 10;
}

/**
 * Calcule le Score de Pression de Trésorerie (SPT)
 * 1 = Faible (6+ mois)
 * 2 = Moyen (3-6 mois)
 * 3 = Élevé (1-3 mois)
 * 4 = Critique (< 1 mois)
 */
export function calculateSPT(
  cash: number,
  monthlyExpenses: number
): 1 | 2 | 3 | 4 {
  const survivalMonths = calculateSurvivalMonths(cash, monthlyExpenses);

  if (survivalMonths >= 6) return 1;
  if (survivalMonths >= 3) return 2;
  if (survivalMonths >= 1) return 3;
  return 4;
}

/**
 * Calcule la probabilité dynamique de conversion (0-1)
 */
export function calculateDynamicProbability(
  sptScore: number,
  imfScore: number,
  declicReached: boolean,
  status: CRMProspectStatus
): number {
  let probability = 0.5; // Base 50%

  // Ajustement SPT (pression = urgence = probabilité)
  switch (sptScore) {
    case 4:
      probability += 0.3; // Critique +30%
      break;
    case 3:
      probability += 0.2; // Élevé +20%
      break;
    case 2:
      probability += 0.1; // Moyen +10%
      break;
  }

  // Ajustement IMF (maturité financière)
  if (imfScore <= 2) {
    probability += 0.15; // Faible IMF = besoin fort
  } else if (imfScore === 3) {
    probability += 0.05; // Moyen IMF = léger besoin
  } else {
    probability -= 0.05; // Bon IMF = moins urgent
  }

  // Bonus déclic
  if (declicReached) {
    probability += 0.2;
  }

  // Ajustement statut
  const statusBonus: Record<CRMProspectStatus, number> = {
    insomnie_detectee: 0,
    probleme_formule: 0,
    demo_realisee: 0.1,
    projection_envoyee: 0.15,
    decision_en_cours: 0.2,
    pilote_actif: 0.25,
    active: 1.0,
    perdu: 0.0,
  };

  probability += statusBonus[status] || 0;

  // Normaliser entre 0 et 1
  return Math.max(0, Math.min(1, Math.round(probability * 100) / 100));
}

/**
 * Récupère tous les prospects
 */
export async function getAllProspects(): Promise<CRMProspect[]> {
  const { data, error } = await supabase
    .from('crm_prospects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching prospects:', error);
    throw error;
  }

  return data || [];
}

/**
 * Récupère un prospect par ID
 */
export async function getProspectById(id: string): Promise<CRMProspect | null> {
  const { data, error } = await supabase
    .from('crm_prospects')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching prospect:', error);
    throw error;
  }

  return data;
}

/**
 * Crée un nouveau prospect
 */
export async function createProspect(
  prospect: Omit<CRMProspect, 'id' | 'created_at' | 'updated_at'>
): Promise<CRMProspect> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('crm_prospects')
    .insert({
      ...prospect,
      created_by: user?.user?.id,
      last_modified_by: user?.user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating prospect:', error);
    throw error;
  }

  return data;
}

/**
 * Met à jour un prospect
 */
export async function updateProspect(
  id: string,
  updates: Partial<CRMProspect>
): Promise<CRMProspect> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('crm_prospects')
    .update({
      ...updates,
      last_modified_by: user?.user?.id,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating prospect:', error);
    throw error;
  }

  return data;
}

/**
 * Supprime un prospect
 */
export async function deleteProspect(id: string): Promise<void> {
  const { error } = await supabase
    .from('crm_prospects')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting prospect:', error);
    throw error;
  }
}

/**
 * Récupère les statistiques du pipeline
 */
export async function getPipelineStats(): Promise<PipelineStats[]> {
  const { data, error } = await supabase.rpc('get_crm_pipeline');

  if (error) {
    console.error('Error fetching pipeline stats:', error);
    throw error;
  }

  return data || [];
}

/**
 * Récupère les analytics CRM
 */
export async function getCRMAnalytics(): Promise<CRMAnalytics> {
  const { data, error } = await supabase
    .from('crm_analytics')
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('Error fetching CRM analytics:', error);
    throw error;
  }

  return data || {
    total_prospects: 0,
    active_pipeline: 0,
    spt_critical: 0,
    spt_high: 0,
    spt_medium: 0,
    spt_low: 0,
    total_potential_mrr: 0,
    weighted_mrr: 0,
    avg_stress_level: 0,
    avg_conversion_probability: 0,
  };
}

/**
 * Récupère les prospects prioritaires
 */
export async function getPriorityProspects(
  limit: number = 10
): Promise<CRMProspect[]> {
  const { data, error } = await supabase.rpc('get_priority_prospects', {
    limit_count: limit,
  });

  if (error) {
    console.error('Error fetching priority prospects:', error);
    throw error;
  }

  return data || [];
}

/**
 * Récupère les interactions d'un prospect
 */
export async function getProspectInteractions(
  prospectId: string
): Promise<CRMInteraction[]> {
  const { data, error } = await supabase
    .from('crm_interactions')
    .select('*')
    .eq('prospect_id', prospectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching interactions:', error);
    throw error;
  }

  return data || [];
}

/**
 * Crée une interaction
 */
export async function createInteraction(
  interaction: Omit<CRMInteraction, 'id' | 'created_at' | 'created_by'>
): Promise<CRMInteraction> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('crm_interactions')
    .insert({
      ...interaction,
      created_by: user?.user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating interaction:', error);
    throw error;
  }

  return data;
}

/**
 * Récupère les notes d'un prospect
 */
export async function getProspectNotes(prospectId: string): Promise<CRMNote[]> {
  const { data, error } = await supabase
    .from('crm_notes')
    .select('*')
    .eq('prospect_id', prospectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }

  return data || [];
}

/**
 * Crée une note
 */
export async function createNote(
  note: Omit<CRMNote, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'last_modified_by'>
): Promise<CRMNote> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('crm_notes')
    .insert({
      ...note,
      created_by: user?.user?.id,
      last_modified_by: user?.user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating note:', error);
    throw error;
  }

  return data;
}

/**
 * Met à jour une note
 */
export async function updateNote(
  id: string,
  updates: Partial<CRMNote>
): Promise<CRMNote> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('crm_notes')
    .update({
      ...updates,
      last_modified_by: user?.user?.id,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating note:', error);
    throw error;
  }

  return data;
}

/**
 * Supprime une note
 */
export async function deleteNote(id: string): Promise<void> {
  const { error } = await supabase
    .from('crm_notes')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
}
